import { useState } from "react";
import React from "react";
import { pdf, Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import CarnetTrasero from "../../styles/assets/img/CarnetTrasero.jpg";

const W = 93.0;
const H = 126.0;
const HALF = 63.0;

const PX_X = W / 728;
const PX_Y = HALF / 492;

const LINE_TOP = (i) => (60 + i * 31.9) * PX_Y;
const COL_PROD = 40 * PX_X;
const COL_ESTIM = 280 * PX_X;
const COL_MESES = 530 * PX_X;

const TOP_EMITIDO = 395 * PX_Y;
const TOP_VENCE = 440 * PX_Y;
const LEFT_VALOR = 160 * PX_X;

const mm = (v) => `${v.toFixed(3)}mm`;
const MM_TO_PT = 2.8346;
const W_PT = W * MM_TO_PT;
const H_PT = H * MM_TO_PT;
const HALF_PT = HALF * MM_TO_PT;
const pt = (mmVal) => mmVal * MM_TO_PT;

const pdfStyles = StyleSheet.create({
  page: { width: W_PT, height: H_PT, backgroundColor: "#ffffff", position: "relative", transform: "rotate(180deg)" },
  field: { position: "absolute", fontSize: 6.5, fontFamily: "Helvetica", color: "#000000" },
});

// ── Datos vacíos para resetear ────────────────────────────────────────────────
const filasVacias = () => Array.from({ length: 10 }, () => ({ producto: "", produccion: "", meses: "" }));

// ── PDF ───────────────────────────────────────────────────────────────────────
function RenovacionPDF({ filas, emitido, vence, seccion }) {
  const offsetPT = seccion === "superior" ? 0 : HALF_PT;

  return (
    <Document>
      <Page size={[W_PT, H_PT]} style={pdfStyles.page} rotate={0}>
        {filas.map((fila, i) => {
          if (!fila.producto && !fila.produccion && !fila.meses) return null;
          return (
            <View key={i}>
              {fila.producto && <Text style={[pdfStyles.field, { top: pt(offsetPT / MM_TO_PT + LINE_TOP(i)), left: pt(COL_PROD) }]}>{fila.producto}</Text>}
              {fila.produccion && <Text style={[pdfStyles.field, { top: pt(offsetPT / MM_TO_PT + LINE_TOP(i)), left: pt(COL_ESTIM) }]}>{fila.produccion}</Text>}
              {fila.meses && <Text style={[pdfStyles.field, { top: pt(offsetPT / MM_TO_PT + LINE_TOP(i)), left: pt(COL_MESES) }]}>{fila.meses}</Text>}
            </View>
          );
        })}
        {emitido && <Text style={[pdfStyles.field, { top: pt(offsetPT / MM_TO_PT + TOP_EMITIDO), left: pt(LEFT_VALOR) }]}>{emitido}</Text>}
        {vence && <Text style={[pdfStyles.field, { top: pt(offsetPT / MM_TO_PT + TOP_VENCE), left: pt(LEFT_VALOR) }]}>{vence}</Text>}
      </Page>
    </Document>
  );
}

async function prepararImpresion(filas, emitido, vence, seccion, setPdfUrl, setShowModal) {
  const blob = await pdf(<RenovacionPDF filas={filas} emitido={emitido} vence={vence} seccion={seccion} />).toBlob();
  setPdfUrl(URL.createObjectURL(blob));
  setShowModal(true);
}

function ejecutarImpresion(pdfUrl, onDone) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = pdfUrl;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        if (onDone) onDone();
      }, 1500);
    }, 500);
  };
}

function validarFecha(str) {
  if (!str) return true;
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return false;
  const [, dd, mm, yyyy] = m;
  const d = new Date(+yyyy, +mm - 1, +dd);
  return d.getFullYear() === +yyyy && d.getMonth() === +mm - 1 && d.getDate() === +dd;
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: #f0f4f0; }

  .app-wrapper { min-height: 100vh; display: grid; grid-template-columns: 480px 1fr; }

  .form-panel {
    background: #1a1a2e; padding: 28px 24px;
    display: flex; flex-direction: column; gap: 14px; overflow-y: auto;
  }
  .form-panel h1 { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800; color: #fff; line-height: 1.25; }
  .form-panel h1 span { color: #d4a017; }

  .form-section {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 9px; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;
  }
  .form-section h3 {
    font-family: 'Syne', sans-serif; font-size: 9px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase; color: #d4a017; margin-bottom: 2px;
  }

  .col-headers { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; padding: 0 2px; }
  .col-header { font-size: 8px; font-weight: 700; color: rgba(255,255,255,0.4); letter-spacing: 0.5px; text-transform: uppercase; text-align: center; }

  .data-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; align-items: center; }

  .form-input {
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.13);
    border-radius: 4px; padding: 5px 7px; color: #fff;
    font-family: 'DM Sans', sans-serif; font-size: 11px; outline: none; width: 100%; transition: border-color .2s;
  }
  .form-input:focus { border-color: #d4a017; }
  .form-input.error { border-color: #e74c3c; }

  .fecha-row { display: flex; flex-direction: column; gap: 3px; }
  .fecha-row label { font-size: 9px; color: rgba(255,255,255,0.4); letter-spacing: 0.8px; text-transform: uppercase; }
  .fecha-error { font-size: 9px; color: #e74c3c; margin-top: 2px; }

  .seccion-selector { display: flex; gap: 10px; }
  .seccion-btn {
    flex: 1; padding: 10px; border-radius: 7px; border: 2px solid rgba(255,255,255,0.15);
    background: transparent; color: rgba(255,255,255,0.5);
    font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700;
    cursor: pointer; transition: all .2s; text-transform: uppercase; letter-spacing: 1px;
  }
  .seccion-btn.active { border-color: #d4a017; background: rgba(212,160,23,0.12); color: #d4a017; }

  .btn-print {
    background: #d4a017; color: #1a1a2e; border: none; border-radius: 7px; padding: 12px 18px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    box-shadow: 0 4px 14px rgba(212,160,23,0.3); transition: transform .15s;
  }
  .btn-print:hover { transform: translateY(-1px); }
  .btn-print:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .hint { font-size: 10px; color: rgba(255,255,255,0.28); line-height: 1.65; }
  .hint b { color: rgba(255,255,255,0.5); }

  .preview-panel {
    background: #d8e0d8; display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start; padding: 36px 32px; gap: 16px; overflow-y: auto;
  }
  .prev-lbl {
    font-family: 'Syne', sans-serif; font-size: 9px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase; color: #2d6a4f; align-self: flex-start;
  }

  .pv-field {
    position: absolute; font-family: Arial, sans-serif; font-size: 6pt;
    color: #0000cc; font-weight: 700; white-space: nowrap; pointer-events: none; z-index: 2;
  }

  .zona-activa {
    position: absolute; left: 0; width: 100%;
    background: rgba(212,160,23,0.08); border: 1.5px dashed rgba(212,160,23,0.5);
    pointer-events: none; z-index: 1;
  }

  /* ── MODAL ── */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.75);
    z-index: 9999; display: flex; align-items: center; justify-content: center;
  }
  .modal-box {
    background: #1a1a2e; border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px; padding: 28px 32px; max-width: 420px; width: 90%;
    display: flex; flex-direction: column; gap: 16px;
  }
  .modal-box h2 { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 800; color: #fff; }
  .modal-box h2 span { color: #d4a017; }
  .modal-step { display: flex; align-items: flex-start; gap: 12px; font-size: 13px; color: rgba(255,255,255,0.75); line-height: 1.5; }
  .modal-step-num {
    background: #d4a017; color: #1a1a2e; font-family: 'Syne', sans-serif; font-weight: 800;
    font-size: 12px; width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .modal-step b { color: #fff; }
  .modal-actions { display: flex; gap: 10px; margin-top: 4px; }
  .modal-btn-primary {
    flex: 1; border: none; border-radius: 7px; padding: 11px 16px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; cursor: pointer;
    background: #d4a017; color: #1a1a2e;
  }
  .modal-btn-secondary {
    background: transparent; color: rgba(255,255,255,0.5);
    border: 1px solid rgba(255,255,255,0.2); border-radius: 7px; padding: 11px 16px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; text-decoration: none;
    display: flex; align-items: center; justify-content: center;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 900px) {
    .app-wrapper { grid-template-columns: 1fr; }
    .form-panel { padding: 20px 16px; }
    .preview-panel { padding: 24px 16px; align-items: center; }
    .prev-lbl { align-self: center; }
  }
  @media (max-width: 600px) {
    .form-panel h1 { font-size: 15px; }
    .form-section { padding: 10px 11px; }
    .col-headers { display: none; }
    .data-row { grid-template-columns: 1fr; gap: 4px; border-bottom: 1px solid rgba(255,255,255,0.07); padding-bottom: 8px; }
    .seccion-selector { flex-direction: column; }
    .seccion-btn { padding: 12px; font-size: 13px; }
    .modal-box { padding: 20px 16px; }
    .modal-actions { flex-direction: column; }
    .modal-btn-primary, .modal-btn-secondary { width: 100%; justify-content: center; }
  }

  @media print { .app-wrapper { display: none !important; } }
`;

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function PrintRenovacion() {
  const [filas, setFilas] = useState(filasVacias());
  const [emitido, setEmitido] = useState("");
  const [vence, setVence] = useState("");
  const [seccion, setSeccion] = useState("superior");
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const setFila = (i, campo, valor) =>
    setFilas(prev => prev.map((f, idx) => idx === i ? { ...f, [campo]: valor } : f));

  const limpiarTodo = () => {
    setFilas(filasVacias());
    setEmitido("");
    setVence("");
  };

  const emitidoOk = validarFecha(emitido);
  const venceOk = validarFecha(vence);
  const puedeImprimir = emitidoOk && venceOk;

  const PREV_W_PX = 300;
  const PREV_SCALE = PREV_W_PX / 728;
  const PREV_H_PX = Math.round(984 * PREV_SCALE);
  const PREV_HALF = Math.round(492 * PREV_SCALE);
  const prevPx = (mmVal) => (mmVal / W) * PREV_W_PX;
  const prevOffsetPx = seccion === "superior" ? 0 : PREV_HALF;

  return (
    <>
      <style>{css}</style>

      <div className="app-wrapper">

        {/* ── FORMULARIO ── */}
        <div className="form-panel">
          <h1>Renovación Carné<br /><span>JNFA — Ferias del Agricultor</span></h1>

          <div className="form-section">
            <h3>¿Dónde imprimir?</h3>
            <div className="seccion-selector">
              <button className={`seccion-btn ${seccion === "superior" ? "active" : ""}`} onClick={() => setSeccion("superior")}>
                ⬆ Sección Superior
              </button>
              <button className={`seccion-btn ${seccion === "inferior" ? "active" : ""}`} onClick={() => setSeccion("inferior")}>
                ⬇ Sección Inferior
              </button>
            </div>
            <p className="hint">Solo se imprime en la sección <b>{seccion === "superior" ? "SUPERIOR" : "INFERIOR"}</b> del carné.</p>
          </div>

          <div className="form-section">
            <h3>Productos (máx. 10)</h3>
            <div className="col-headers">
              <span className="col-header">Productos Autorizados</span>
              <span className="col-header">Producción Estimada</span>
              <span className="col-header">Meses Producción</span>
            </div>
            {filas.map((fila, i) => (
              <div className="data-row" key={i}>
                <input className="form-input" placeholder={`Producto ${i + 1}`} value={fila.producto} onChange={e => setFila(i, "producto", e.target.value)} />
                <input className="form-input" placeholder="Ej: 200 kg" value={fila.produccion} onChange={e => setFila(i, "produccion", e.target.value)} />
                <input className="form-input" placeholder="Ej: 6 meses" value={fila.meses} onChange={e => setFila(i, "meses", e.target.value)} />
              </div>
            ))}
          </div>

          <div className="form-section">
            <h3>Vigencia</h3>
            <div className="fecha-row">
              <label>Emitido el (DD-MM-YYYY)</label>
              <input className={`form-input ${emitido && !emitidoOk ? "error" : ""}`} placeholder="27-08-2025" value={emitido} onChange={e => setEmitido(e.target.value)} maxLength={10} />
              {emitido && !emitidoOk && <span className="fecha-error">⚠ Formato inválido. Usa DD-MM-YYYY</span>}
            </div>
            <div className="fecha-row">
              <label>Vence el (DD-MM-YYYY)</label>
              <input className={`form-input ${vence && !venceOk ? "error" : ""}`} placeholder="31-12-2025" value={vence} onChange={e => setVence(e.target.value)} maxLength={10} />
              {vence && !venceOk && <span className="fecha-error">⚠ Formato inválido. Usa DD-MM-YYYY</span>}
            </div>
          </div>

          <button className="btn-print" disabled={!puedeImprimir}
            onClick={() => prepararImpresion(filas, emitido, vence, seccion, setPdfUrl, setShowModal)}>
            🖨️&nbsp; Imprimir Renovación
          </button>

          {!puedeImprimir && (emitido || vence) && (
            <p className="hint" style={{ color: "#e74c3c" }}>Corrige las fechas antes de imprimir.</p>
          )}

          <p className="hint">Hoja <b>93 × 126mm</b> · Sección <b>{seccion === "superior" ? "superior" : "inferior"}</b>.</p>
        </div>

        {/* ── PREVIEW ── */}
        <div className="preview-panel">
          <p className="prev-lbl">Vista previa — Carné trasero</p>
          <p style={{
            fontSize: 10, color: "#556b55", fontFamily: "monospace", alignSelf: "flex-start",
            background: "rgba(0,0,0,0.06)", padding: "3px 8px", borderRadius: 3, borderLeft: "2px solid #d4a017"
          }}>
            Zona <b>{seccion === "superior" ? "SUPERIOR" : "INFERIOR"}</b> activa (destacada en dorado)
          </p>

          <h4 style={{ color: "red" }} >Introducir en impresora en esta misma dirección</h4>


          <div style={{
            position: "relative", width: PREV_W_PX, height: PREV_H_PX,
            boxShadow: "0 6px 24px rgba(0,0,0,0.28)", outline: "1.5px solid rgba(212,160,23,0.6)", overflow: "hidden"
          }}>
            <img src={CarnetTrasero} alt="Plantilla trasera" style={{ width: PREV_W_PX, height: PREV_H_PX, objectFit: "fill", display: "block" }} />
            <div className="zona-activa" style={{ top: prevOffsetPx, height: PREV_HALF }} />
            {filas.map((fila, i) => {
              const topPx = prevOffsetPx + prevPx(LINE_TOP(i));
              return (
                <span key={i}>
                  {fila.producto && <span className="pv-field" style={{ top: topPx, left: prevPx(COL_PROD) }}>{fila.producto}</span>}
                  {fila.produccion && <span className="pv-field" style={{ top: topPx, left: prevPx(COL_ESTIM) }}>{fila.produccion}</span>}
                  {fila.meses && <span className="pv-field" style={{ top: topPx, left: prevPx(COL_MESES) }}>{fila.meses}</span>}
                </span>
              );
            })}
            {emitido && <span className="pv-field" style={{ top: prevOffsetPx + prevPx(TOP_EMITIDO), left: prevPx(LEFT_VALOR) }}>{emitido}</span>}
            {vence && <span className="pv-field" style={{ top: prevOffsetPx + prevPx(TOP_VENCE), left: prevPx(LEFT_VALOR) }}>{vence}</span>}
          </div>
        </div>
      </div>

      {/* ── MODAL INSTRUCCIONES ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h2>Imprimir Renovación <span>JNFA</span></h2>
            <div className="modal-step"><div className="modal-step-num">1</div>
              <div>PDF en <b>93 × 126mm</b> · Sección <b>{seccion === "superior" ? "SUPERIOR" : "INFERIOR"}</b>.</div>
            </div>
            <div className="modal-step"><div className="modal-step-num">2</div>
              <div>Descárgalo y ábrelo con <b>Adobe Reader</b> → imprime sin configurar nada.</div>
            </div>
            <div className="modal-step"><div className="modal-step-num">3</div>
              <div>O usa <b>"Imprimir ahora"</b> y verifica tamaño <b>93 × 126mm</b>, escala <b>100%</b>.</div>
            </div>
            <div className="modal-actions">
              <button className="modal-btn-primary" onClick={() => {
                setShowModal(false);
                ejecutarImpresion(pdfUrl, () => setShowConfirm(true));
              }}>
                🖨️ Imprimir ahora
              </button>
              <a className="modal-btn-secondary" href={pdfUrl} download="renovacion_JNFA.pdf" onClick={() => setShowModal(false)}>
                📥 Descargar PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CONFIRMACIÓN POST-IMPRESIÓN ── */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ alignItems: "center", textAlign: "center" }}>
            <div style={{ fontSize: 40 }}>🖨️</div>
            <h2>¿La impresión fue <span>exitosa</span>?</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}>
              Si la renovación se imprimió correctamente, los campos se limpiarán.<br />
              Si hubo algún problema, se mantendrán para volver a intentarlo.
            </p>
            <div className="modal-actions" style={{ width: "100%" }}>
              <button className="modal-btn-primary" style={{ background: "#27ae60" }}
                onClick={() => { limpiarTodo(); setShowConfirm(false); }}>
                ✅ Sí, limpiar campos
              </button>
              <button className="modal-btn-secondary" style={{ color: "#e74c3c", borderColor: "#e74c3c" }}
                onClick={() => setShowConfirm(false)}>
                ❌ No, mantener datos
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
